/**
 * The 2am dot visual: a dense ball with six arms along the axes, seen
 * isometrically so the arms fall at 60-degree intervals. Lilac is the 2am
 * brand colour, the one non-palette colour on the site (it only sits on the
 * dark slab).
 */
import React from "react";
import { drawDotField } from "./dotField";
import { dotMotion, randomInCylinder, randomInSphere, rotateX, rotateY } from "./dotGeometry";
import useDotCanvas from "./useDotCanvas";

const CENTER_DOTS = 160;
const DOTS_PER_ARM = 90;
const CENTER_R = 0.28;
const ARM_TUBE_R = 0.14;
const ARM_START = 0.2;
const ARM_END = 0.72;
/** @type {Array<[number, number, number]>} */
const ARM_AXES = [
	[1, 0, 0],
	[-1, 0, 0],
	[0, 1, 0],
	[0, -1, 0],
	[0, 0, 1],
	[0, 0, -1],
];
const YAW = Math.PI / 4;
const PITCH = Math.atan(1 / Math.SQRT2);

/** The hexapod's dots: a centre ball plus one tube per arm. */
export function buildHexapodDots() {
	const dots = [];
	for (let i = 0; i < CENTER_DOTS; i++) {
		const [x, y, z] = randomInSphere(CENTER_R);
		dots.push({ x, y, z, ...dotMotion() });
	}
	for (const axis of ARM_AXES) {
		for (let i = 0; i < DOTS_PER_ARM; i++) {
			const [x, y, z] = randomInCylinder(axis, ARM_TUBE_R, ARM_START, ARM_END);
			dots.push({ x, y, z, ...dotMotion() });
		}
	}
	return dots;
}

const HEXAPOD_LOOK = {
	color: "#C490F8",
	radiusScale: 0.44,
	breathAmp: 0.05,
	pushScale: 0.08,
	project: ([x, y, z]) => rotateX(...rotateY(x, y, z, YAW), PITCH),
};

/** Draws one hexapod frame (see drawDotField). */
export const drawHexapodFrame = (ctx, state, t, animate) =>
	drawDotField(ctx, state, t, animate, HEXAPOD_LOOK);

/**
 * The 2am hexapod canvas; it fills its positioned parent.
 * @param {{ className?: string }} props
 */
const HexapodDots = ({ className = "" }) => {
	const canvasRef = useDotCanvas(buildHexapodDots, drawHexapodFrame);
	return (
		<canvas
			ref={canvasRef}
			className={`block w-full h-full ${className}`.trim()}
			aria-hidden="true"
		/>
	);
};

export default HexapodDots;
